import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
    Search,
    MoreHorizontal,
    ChevronsLeft,
    ChevronLeft,
    ChevronRight,
    ChevronsRight,
    ArrowUpDown,
    PackageSearch,
} from "lucide-react";
import { toast } from "sonner";

import { EmptyState } from "@/components/feedback/EmptyState.tsx";
import { DateRangeFilter } from "@/components/filters/DateRangeFilter";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { TableSkeleton } from "@/components/loading/TableSkeleton.tsx";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
    formatDateForDisplay,
    isDateWithinRange,
    getOrders,
    saveOrders,
    fetchCustomers,
    fetchOrders,
    enrichOrdersWithCustomers,
    type OrderRecord,
} from "@/lib/utils";
import type { ClientRecord } from "@/lib/utils";
import { getAuthenticatedUser } from "@/lib/auth";

// ----------------------------------------------------------------------------

interface OrdiniProps {
    className?: string;
}

export function Ordini(props: OrdiniProps) {
    // -------------------------------------
    // Props destructuring
    // -------------------------------------

    const { className } = props;

    // -------------------------------------
    // Hooks (e.g. useState, useMemo ...)
    // -------------------------------------

    const navigate = useNavigate();
    const [orders, setOrders] = useState<OrderRecord[]>(() => getOrders());
    const [clients, setClients] = useState<ClientRecord[]>([]);
    // Only show clients that have at least one order
    const clientOptions = useMemo(
        () =>
            clients.filter((c) =>
                orders.some(
                    (o) =>
                        o.clientId === c.id ||
                        (o.email &&
                            c.email &&
                            o.email.toLowerCase().trim() ===
                                c.email.toLowerCase().trim()) ||
                        (o.customerName &&
                            c.name &&
                            o.customerName.toLowerCase().trim() ===
                                c.name.toLowerCase().trim()),
                ),
            ),
        [clients, orders],
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const [startDate, setStartDate] = useState(`${currentYear - 1}-01-01`);
    const [endDate, setEndDate] = useState(`${currentYear + 1}-12-31`);
    const [orderSearchQuery, setOrderSearchQuery] = useState("");
    const [isLoadingOrders, setIsLoadingOrders] = useState(true);
    const [ordersError, setOrdersError] = useState<string | null>(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const selectedClientId = searchParams.get("clientId") ?? "all";

    const currentUser = useMemo(() => getAuthenticatedUser(), []);
    const canViewAll = 
        currentUser?.canViewAll ||
        currentUser?.id?.toLowerCase() === "dev-nogravity"
    ;
    const agentTag = currentUser?.agentTag;
    const agentCode = currentUser?.agentCode;

    // Filter clients belonging to the logged-in agent
    const agentClients = useMemo(() => {
        if (canViewAll) return clients.filter((c) => !c.isAgent);
        if (!agentTag && !agentCode) return [];
        const tagLower = agentTag ? agentTag.toLowerCase().trim() : "";
        const codeStr = agentCode ? agentCode.trim() : "";

        return clients.filter((c) => {
            if (c.isAgent === true) return false;
            const tags = (c.tags ?? []).map((t) => t.toLowerCase().trim());
            return tags.some((t) => {
                if (tagLower && t === tagLower) return true;
                if (
                    codeStr &&
                    (t === `agente-${codeStr}` || t === `agent-${codeStr}`)
                )
                    return true;
                return false;
            });
        });
    }, [clients, canViewAll, agentTag, agentCode]);

    // Extract set of IDs, emails, and names for matching orders
    const agentIdentifiers = useMemo(() => {
        if (canViewAll) return null;
        if (!agentTag && !agentCode) {
            return { ids: new Set<string>(), emails: new Set<string>(), names: new Set<string>() };
        }
        const ids = new Set<string>();
        const emails = new Set<string>();
        const names = new Set<string>();

        agentClients.forEach((c) => {
            if (c.id) ids.add(c.id.toLowerCase());
            if (c.email) emails.add(c.email.toLowerCase());
            if (c.name) names.add(c.name.toLowerCase());
        });

        if (currentUser?.fullName) {
            names.add(currentUser.fullName.toLowerCase());
        }

        return { ids, emails, names };
    }, [agentClients, canViewAll, agentTag, agentCode, currentUser]);

    const filteredOrders = useMemo(
        () => {
            const selectedClient =
                selectedClientId !== "all"
                    ? clients.find((c) => c.id === selectedClientId)
                    : null;

            return orders.filter((order) => {
                // Filter out cancelled / aborted / archived orders
                const s = order.status;
                if (
                    s === 6 ||
                    s === 7 ||
                    (typeof s === "string" &&
                        (s.toLowerCase() === "cancelled" ||
                            s.toLowerCase() === "annullato" ||
                            s.toLowerCase() === "aborted" ||
                            s.toLowerCase() === "archived"))
                ) {
                    return false;
                }

                // Scoping for authenticated agent
                if (agentIdentifiers) {
                    const matchesId =
                        order.clientId &&
                        agentIdentifiers.ids.has(order.clientId.toLowerCase());
                    const matchesEmail =
                        order.email &&
                        agentIdentifiers.emails.has(order.email.toLowerCase());
                    const matchesName =
                        order.customerName &&
                        agentIdentifiers.names.has(
                            order.customerName.toLowerCase(),
                        );

                    if (!matchesId && !matchesEmail && !matchesName) {
                        return false;
                    }
                }

                const matchesSelectedClient =
                    selectedClientId === "all" ||
                    order.clientId === selectedClientId ||
                    (selectedClient && (
                        (order.email &&
                            selectedClient.email &&
                            order.email.toLowerCase().trim() ===
                                selectedClient.email.toLowerCase().trim()) ||
                        (order.customerName &&
                            selectedClient.name &&
                            order.customerName.toLowerCase().trim() ===
                                selectedClient.name.toLowerCase().trim())
                    ));

                return (
                    isDateWithinRange(order.date, startDate, endDate) &&
                    matchesSelectedClient &&
                    (orderSearchQuery.trim().length === 0 ||
                        order.id
                            .toLowerCase()
                            .includes(orderSearchQuery.trim().toLowerCase()) ||
                        order.customerName
                            ?.toLowerCase()
                            .includes(orderSearchQuery.trim().toLowerCase()) ||
                        order.email
                            ?.toLowerCase()
                            .includes(orderSearchQuery.trim().toLowerCase()))
                );
            });
        },
        [
            orders,
            endDate,
            orderSearchQuery,
            selectedClientId,
            startDate,
            agentIdentifiers,
            clients,
        ],
    );

    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
    const paginatedOrders = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return filteredOrders.slice(start, start + pageSize);
    }, [filteredOrders, currentPage, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [startDate, endDate, orderSearchQuery, selectedClientId]);

    // -------------------------------------
    // Effects
    // -------------------------------------

    useEffect(() => {
        let isMounted = true;

        async function loadOrdersData(): Promise<void> {
            setIsLoadingOrders(true);
            setOrdersError(null);

            try {
                const activeAgent = canViewAll ? "" : (agentTag || (agentCode ? `agente-${agentCode}` : "none"));
                const [apiOrders, apiClients] = await Promise.all([
                    fetchOrders(undefined, activeAgent),
                    fetchCustomers(activeAgent),
                ]);

                if (!isMounted) {
                    return;
                }

                const isStoredenOrderId = (id: string) =>
                    /^\d+_[a-z0-9]+$/i.test(id) || /^\d{4,}$/.test(id);

                const localOrders = getOrders();
                const apiOrderIds = new Set(apiOrders.map((order) => order.id));
                const localOnlyOrders = localOrders.filter((order) => {
                    const s = Number(order.status);
                    if (s === 6 || s === 7) return false;
                    return !isStoredenOrderId(order.id) && !apiOrderIds.has(order.id);
                });

                const rawMergedOrders = [...localOnlyOrders, ...apiOrders];
                const mergedOrders = enrichOrdersWithCustomers(rawMergedOrders, apiClients);
                setOrders(mergedOrders);
                saveOrders(mergedOrders);
                setClients(apiClients);
            } catch {
                if (!isMounted) {
                    return;
                }

                setOrders(getOrders());
                setClients([]);
                setOrdersError(
                    "API ordini non disponibile al momento. Sto mostrando i dati frontend di fallback.",
                );
                toast.warning(
                    "Stai lavorando con gli ordini locali di fallback",
                    {
                        id: "orders-fallback-warning",
                        description: "Impossibile contattare il server ngrok.",
                    },
                );
            } finally {
                if (isMounted) {
                    setIsLoadingOrders(false);
                }
            }
        }

        void loadOrdersData();

        return () => {
            isMounted = false;
        };
    }, []);

    // -------------------------------------
    // Component functions
    // -------------------------------------

    function handleClientFilterChange(value: string) {
        const nextParams = new URLSearchParams(searchParams);

        if (value === "all") {
            nextParams.delete("clientId");
        } else {
            nextParams.set("clientId", value);
        }

        setSearchParams(nextParams);
    }

    function handleResetFilters() {
        setStartDate("2025-01-01");
        setEndDate("2026-12-31");
        setOrderSearchQuery("");
        setSearchParams(new URLSearchParams());
    }

    // -------------------------------------
    // Component local variables
    // -------------------------------------

    const selectedClient = clients.find(
        (client) => client.id === selectedClientId,
    );

    return (
        <DashboardLayout className={className}>
            <div className="w-full">
                <PageBreadcrumb
                    items={[{ label: "Home", to: "/" }, { label: "Ordini" }]}
                />

                <div className="border-b border-gray-200 mb-6 w-full" />

                {/* Main Card */}
                <Card className="shadow-sm border-gray-100">
                    <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-6">
                        <div>
                            <CardTitle className="text-2xl font-bold mb-1">
                                Ordini
                            </CardTitle>
                            <CardDescription className="text-sm text-gray-500">
                                Gestisci i tuoi ordini clienti
                            </CardDescription>
                        </div>
                        <div className="flex w-full sm:w-auto flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <DateRangeFilter
                                startDate={startDate}
                                endDate={endDate}
                                onStartDateChange={setStartDate}
                                onEndDateChange={setEndDate}
                            />
                            {/*<Button className="bg-[#0D6EFD] hover:bg-blue-700 text-white h-9 px-6 font-medium">
                Download
              </Button>*/}
                        </div>
                    </CardHeader>

                    <CardContent className="space-y-4">
                        {ordersError && (
                            <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                                {ordersError}
                            </div>
                        )}

                        {/* Filters Row */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            {/*<div className="w-full sm:w-[300px]">
                <Select
                  value={selectedClientId}
                  onValueChange={handleClientFilterChange}
                >
                  <SelectTrigger className="w-full bg-white border-gray-200 text-gray-500 h-9">
                    <SelectValue placeholder="Filtra per cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    clientOptions.map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))
                  </SelectContent>
                </Select>
              </div>*/}

                            <div className="relative w-full sm:w-75">
                                <Input
                                    className="w-full pr-9 bg-white border-gray-200 text-gray-500 h-9"
                                    placeholder="Cerca ordini (ID, cliente, email)"
                                    value={orderSearchQuery}
                                    onChange={(event) =>
                                        setOrderSearchQuery(event.target.value)
                                    }
                                />
                                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            </div>
                        </div>

                        {selectedClient && (
                            <div className="rounded-md border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-900">
                                Stai visualizzando lo storico ordini di{" "}
                                <span className="font-semibold">
                                    {selectedClient.name}
                                </span>
                                .
                            </div>
                        )}

                        {isLoadingOrders && (
                            <TableSkeleton
                                columns={5}
                                rows={pageSize}
                                mobileCards={3}
                            />
                        )}

                        {!isLoadingOrders && (
                            <div className="space-y-3 lg:hidden">
                                {paginatedOrders.map((order) => (
                                    <Card
                                        key={order.id}
                                        className="shadow-sm border-gray-100 hover:bg-gray-50/75 cursor-pointer transition-colors"
                                        onClick={() => navigate(`/ordini/${order.id}`)}>
                                        <CardContent className="p-4">
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="text-xs text-gray-500">
                                                        {formatDateForDisplay(
                                                            order.date,
                                                        )}
                                                    </div>
                                                    <div className="text-base font-semibold text-gray-900 truncate">
                                                        {order.customerName}
                                                    </div>
                                                    <div className="text-sm text-gray-500 truncate">
                                                        {order.email}
                                                    </div>
                                                    <div className="text-sm text-gray-900 mt-1">
                                                        <span className="text-gray-500">
                                                            ID{" "}
                                                        </span>
                                                        <span className="font-medium">
                                                            {order.id}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div
                                                    className="flex items-center gap-2 shrink-0"
                                                    onClick={(e) => e.stopPropagation()}>
                                                    <div className="text-right">
                                                        <div className="text-xs text-gray-500">
                                                            Totale
                                                        </div>
                                                        <div className="text-base font-semibold text-gray-900">
                                                            {order.total}
                                                        </div>
                                                    </div>
                                                    <DropdownMenu>
                                                        <DropdownMenuTrigger
                                                            asChild>
                                                            <Button
                                                                variant="ghost"
                                                                className="h-8 w-8 p-0 hover:bg-gray-100"
                                                                onClick={(e) => e.stopPropagation()}>
                                                                <span className="sr-only">
                                                                    Apri menu
                                                                </span>
                                                                <MoreHorizontal className="h-4 w-4 text-gray-500" />
                                                            </Button>
                                                        </DropdownMenuTrigger>
                                                        <DropdownMenuContent align="end">
                                                            <DropdownMenuItem
                                                                asChild>
                                                                <Link
                                                                    to={`/ordini/${order.id}`}>
                                                                    Visualizza dettaglio
                                                                </Link>
                                                            </DropdownMenuItem>
                                                        </DropdownMenuContent>
                                                    </DropdownMenu>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                                {filteredOrders.length === 0 && (
                                    <EmptyState
                                        icon={PackageSearch}
                                        title="Nessun ordine trovato"
                                        description="Prova a modificare i filtri di data, il cliente selezionato o l'ID ordine cercato."
                                        actionLabel="Pulisci filtri"
                                        onAction={handleResetFilters}
                                        iconClassName="bg-blue-50 text-blue-600"
                                    />
                                )}
                            </div>
                        )}

                        {/* Table */}
                        {!isLoadingOrders && (
                            <div className="hidden lg:block">
                                {filteredOrders.length === 0 ? (
                                    <EmptyState
                                        icon={PackageSearch}
                                        title="Nessun ordine trovato"
                                        description="Prova a modificare i filtri di data, il cliente selezionato o l'ID ordine cercato."
                                        actionLabel="Pulisci filtri"
                                        onAction={handleResetFilters}
                                        iconClassName="bg-blue-50 text-blue-600"
                                    />
                                ) : (
                                    <div className="rounded-md border border-gray-100 overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="hover:bg-transparent border-gray-100">
                                                    <TableHead className="font-medium text-gray-900">
                                                        Data
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900">
                                                        <div className="flex items-center gap-1 cursor-pointer">
                                                            Nome Cliente
                                                            <ArrowUpDown className="w-3 h-3 text-gray-400" />
                                                        </div>
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900">
                                                        <div className="flex items-center gap-1 cursor-pointer">
                                                            Email
                                                            <ArrowUpDown className="w-3 h-3 text-gray-400" />
                                                        </div>
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900">
                                                        ID ordine
                                                    </TableHead>
                                                    <TableHead className="font-medium text-gray-900">
                                                        Totale
                                                    </TableHead>
                                                    <TableHead className="w-12"></TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {paginatedOrders.map(
                                                    (order) => (
                                                        <TableRow
                                                            key={order.id}
                                                            onClick={() => navigate(`/ordini/${order.id}`)}
                                                            className="border-gray-100 hover:bg-gray-50/75 cursor-pointer transition-colors">
                                                            <TableCell className="text-gray-900">
                                                                {formatDateForDisplay(
                                                                    order.date,
                                                                )}
                                                            </TableCell>
                                                            <TableCell className="text-gray-900">
                                                                {
                                                                    order.customerName
                                                                }
                                                            </TableCell>
                                                            <TableCell className="text-gray-900">
                                                                {order.email}
                                                            </TableCell>
                                                            <TableCell className="text-gray-900 font-medium">
                                                                {order.id}
                                                            </TableCell>
                                                            <TableCell className="text-gray-900 font-medium">
                                                                {order.total}
                                                            </TableCell>
                                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                                <DropdownMenu>
                                                                    <DropdownMenuTrigger
                                                                        asChild>
                                                                        <Button
                                                                            variant="ghost"
                                                                            className="h-8 w-8 p-0 hover:bg-gray-100"
                                                                            onClick={(e) => e.stopPropagation()}>
                                                                            <span className="sr-only">
                                                                                Apri
                                                                                menu
                                                                            </span>
                                                                            <MoreHorizontal className="h-4 w-4 text-gray-500" />
                                                                        </Button>
                                                                    </DropdownMenuTrigger>
                                                                    <DropdownMenuContent align="end">
                                                                        <DropdownMenuItem
                                                                            asChild>
                                                                            <Link
                                                                                to={`/ordini/${order.id}`}>
                                                                                Visualizza dettaglio
                                                                            </Link>
                                                                        </DropdownMenuItem>
                                                                    </DropdownMenuContent>
                                                                </DropdownMenu>
                                                            </TableCell>
                                                        </TableRow>
                                                    ),
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Pagination Footer */}
                        <div className="flex flex-col sm:flex-row items-center justify-between pt-2 text-sm text-gray-500 gap-4">
                            <div>
                                Totale: {filteredOrders.length} {filteredOrders.length === 1 ? "ordine" : "ordini"}
                            </div>
                            <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-4 sm:gap-6">
                                <div className="flex items-center gap-2">
                                    <span className="font-medium text-gray-900">
                                        Righe per page
                                    </span>
                                    <Select
                                        value={String(pageSize)}
                                        onValueChange={(val) => {
                                            setPageSize(Number(val));
                                            setCurrentPage(1);
                                        }}>
                                        <SelectTrigger className="h-8 w-16 bg-white border-gray-200">
                                            <SelectValue
                                                placeholder={String(pageSize)}
                                            />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="10">
                                                10
                                            </SelectItem>
                                            <SelectItem value="20">
                                                20
                                            </SelectItem>
                                            <SelectItem value="50">
                                                50
                                            </SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="font-medium text-gray-900">
                                    Page {currentPage} of {totalPages}
                                </div>

                                <div className="flex items-center gap-1">
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage <= 1}
                                        onClick={() => setCurrentPage(1)}>
                                        <ChevronsLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage <= 1}
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.max(1, p - 1),
                                            )
                                        }>
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage >= totalPages}
                                        onClick={() =>
                                            setCurrentPage((p) =>
                                                Math.min(totalPages, p + 1),
                                            )
                                        }>
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-8 w-8 border-gray-200"
                                        disabled={currentPage >= totalPages}
                                        onClick={() =>
                                            setCurrentPage(totalPages)
                                        }>
                                        <ChevronsRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </DashboardLayout>
    );
}
